/**
 * React Query hooks for Redemptions/Vouchers
 * Provides automatic caching, refetching, real-time updates, and cache invalidation
 *
 * Supports server-side pagination and search for scalability with large datasets.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRealtimeSubscription } from "./useRealtimeSubscription";
import * as redemptionsService from "../services/redemptions.service";
import type { PaginatedRedemptionsParams } from "../services/redemptions.service";
import {
  cancelVoucher,
  testVoucher,
  markVoucherVendorPaid,
} from "../services/vouchers.service";
import { useAuth } from "../contexts/AuthContext";
import type { UUID } from "../types/common.types";

/**
 * Query key factory for redemptions/vouchers
 */
export const redemptionKeys = {
  all: ["redemptions"] as const,
  lists: () => [...redemptionKeys.all, "list"] as const,
  list: (userId?: string, userRole?: string) =>
    [...redemptionKeys.lists(), { userId, userRole }] as const,
  paginated: () => [...redemptionKeys.all, "paginated"] as const,
  paginatedWithParams: (
    userId?: string,
    userRole?: string,
    params?: PaginatedRedemptionsParams,
  ) =>
    [...redemptionKeys.paginated(), { userId, userRole, ...params }] as const,
  analytics: () => [...redemptionKeys.all, "analytics"] as const,
  analyticsForUser: (userId?: string, userRole?: string) =>
    [...redemptionKeys.analytics(), { userId, userRole }] as const,
  filterOptions: () => [...redemptionKeys.all, "filterOptions"] as const,
  filterOptionsForUser: (userId?: string, userRole?: string) =>
    [...redemptionKeys.filterOptions(), { userId, userRole }] as const,
  dailyChart: () => [...redemptionKeys.all, "daily"] as const,
  dailyChartForUser: (userId?: string, userRole?: string, days?: number) =>
    [...redemptionKeys.dailyChart(), { userId, userRole, days }] as const,
};

/**
 * Hook to fetch daily redemption chart data
 */
export function useDailyRedemptionData(
  days: number = 7,
  options?: { enableRealtime?: boolean },
) {
  const { user } = useAuth();
  const { enableRealtime = true } = options || {};

  const query = useQuery({
    queryKey: redemptionKeys.dailyChartForUser(user?.id, user?.role, days),
    queryFn: () =>
      redemptionsService.getDailyRedemptionData(user?.id, user?.role, days),
    enabled: !!user,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Subscribe to real-time updates
  useRealtimeSubscription({
    table: "vouchers",
    queryKeys: [redemptionKeys.dailyChart()],
    enabled: enableRealtime && !!user,
  });

  return query;
}

/**
 * Hook to fetch paginated redemptions with server-side search and filtering
 * Designed for scalability with large datasets (100k+ vouchers)
 */
export function usePaginatedRedemptions(
  params: PaginatedRedemptionsParams = {},
  options?: { enableRealtime?: boolean },
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { enableRealtime = true } = options || {};

  const query = useQuery({
    queryKey: redemptionKeys.paginatedWithParams(user?.id, user?.role, params),
    queryFn: () =>
      redemptionsService.getRedemptionsPaginated(user?.id, user?.role, params),
    enabled: !!user,
    // Keep previous data while fetching new page for smooth pagination
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Subscribe to real-time updates
  useRealtimeSubscription({
    table: "vouchers",
    queryKeys: [redemptionKeys.paginated()],
    enabled: enableRealtime && !!user,
    onInsert: () => {
      // Invalidate paginated queries on new voucher
      queryClient.invalidateQueries({ queryKey: redemptionKeys.paginated() });
    },
    onUpdate: () => {
      queryClient.invalidateQueries({ queryKey: redemptionKeys.paginated() });
    },
  });

  return {
    ...query,
    redemptions: query.data?.data || [],
    totalCount: query.data?.totalCount || 0,
    page: query.data?.page || 1,
    pageSize: query.data?.pageSize || 50,
    totalPages: query.data?.totalPages || 0,
    nameLookupFailures: query.data?.nameLookupFailures ?? [],
    hasMore: query.data?.hasMore || false,
  };
}

/**
 * Hook to fetch optimized analytics (uses aggregate queries)
 */
export function useRedemptionAnalyticsOptimized(options?: {
  enableRealtime?: boolean;
}) {
  const { user } = useAuth();
  const { enableRealtime = true } = options || {};

  const query = useQuery({
    queryKey: redemptionKeys.analyticsForUser(user?.id, user?.role),
    queryFn: () =>
      redemptionsService.getRedemptionAnalyticsOptimized(user?.id, user?.role),
    enabled: !!user,
    staleTime: 60 * 1000, // Analytics can be stale longer
    gcTime: 10 * 60 * 1000,
  });

  useRealtimeSubscription({
    table: "vouchers",
    queryKeys: [redemptionKeys.analytics()],
    enabled: enableRealtime && !!user,
  });

  return query;
}

/**
 * Hook to fetch filter options (organizations, voucher types, etc.)
 */
export function useRedemptionFilterOptions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: redemptionKeys.filterOptionsForUser(user?.id, user?.role),
    queryFn: () =>
      redemptionsService.getRedemptionFilterOptions(user?.id, user?.role),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Filter options change rarely
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook to cancel a voucher
 */
export function useCancelVoucher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (voucherId: UUID) => cancelVoucher(voucherId),
    onSuccess: () => {
      // Invalidate all redemption queries to reflect the change
      queryClient.invalidateQueries({ queryKey: redemptionKeys.all });
    },
    onError: (error) => {
      console.error("[Redemptions] Error cancelling voucher:", error);
    },
  });
}

/**
 * Hook to flip vendor_paid on a redeemed voucher.
 *
 * Uses optimistic update so the toggle visually flips on click; the
 * server reconciles asynchronously, and onError rolls back if the
 * RPC fails.
 */
export function useMarkVoucherVendorPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      voucherId,
      vendorPaid,
    }: {
      voucherId: UUID;
      vendorPaid: boolean;
    }) => markVoucherVendorPaid(voucherId, vendorPaid),

    onMutate: async ({ voucherId, vendorPaid }) => {
      // Stop in-flight refetches so they don't clobber our optimistic write
      await queryClient.cancelQueries({ queryKey: redemptionKeys.paginated() });

      // Snapshot every paginated cache page so onError can roll back
      const previous = queryClient.getQueriesData({
        queryKey: redemptionKeys.paginated(),
      });

      // Optimistically patch vendor_paid (+ rough audit field stubs the
      // server will overwrite on the post-mutate invalidate) on the
      // matching voucher in every cached page
      const nowIso = new Date().toISOString();
      queryClient.setQueriesData(
        { queryKey: redemptionKeys.paginated() },
        // deno-lint-ignore no-explicit-any
        (old: any) => {
          if (!old || !Array.isArray(old.data)) {
            return old;
          }
          return {
            ...old,
            // deno-lint-ignore no-explicit-any
            data: old.data.map((v: any) =>
              v?.id === voucherId
                ? {
                    ...v,
                    vendor_paid: vendorPaid,
                    vendor_paid_at: vendorPaid ? nowIso : null,
                    // vendor_paid_by gets the real value on the invalidate
                    // refetch — leave a non-null placeholder for the
                    // truthiness checks in the meantime
                    vendor_paid_by: vendorPaid
                      ? (v.vendor_paid_by ?? "pending")
                      : null,
                  }
                : v,
            ),
          };
        },
      );

      return { previous };
    },

    onError: (error, _vars, context) => {
      console.error("[Redemptions] Error marking vendor_paid:", error);
      // Roll back every cache page from the snapshot
      if (context?.previous) {
        for (const [queryKey, data] of context.previous) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      // Reconcile with server truth (success path picks up real
      // vendor_paid_at + vendor_paid_by; error path corrects any drift)
      queryClient.invalidateQueries({ queryKey: redemptionKeys.all });
    },
  });
}

/**
 * Hook to mark a voucher as test
 */
export function useTestVoucher() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (voucherId: UUID) => testVoucher(voucherId),
    onSuccess: () => {
      // Invalidate all redemption queries to reflect the change
      queryClient.invalidateQueries({ queryKey: redemptionKeys.all });
    },
    onError: (error) => {
      console.error("[Redemptions] Error marking voucher as test:", error);
    },
  });
}

/**
 * Hook to manually invalidate all redemption caches
 * Useful for forcing a refresh after external changes
 */
export function useInvalidateRedemptions() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: redemptionKeys.all });
  };
}

/**
 * Combined hook that provides paginated redemption data with analytics
 * Designed for scalability with large datasets (100k+ vouchers)
 *
 * Features:
 * - Server-side pagination (50 items per page default)
 * - Server-side search (debounce in component)
 * - Server-side filtering (status, date, org, type)
 * - Optimized analytics using aggregate queries
 * - Real-time updates via Supabase subscriptions
 */
export function usePaginatedRedemptionsWithAnalytics(
  params: PaginatedRedemptionsParams = {},
  options?: { enableRealtime?: boolean },
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { enableRealtime = true } = options || {};

  const paginated = usePaginatedRedemptions(params, { enableRealtime });
  const analytics = useRedemptionAnalyticsOptimized({ enableRealtime: false });
  const filterOptions = useRedemptionFilterOptions();
  const cancelVoucherMutation = useCancelVoucher();
  const testVoucherMutation = useTestVoucher();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: redemptionKeys.all });
  };

  return {
    // Paginated data
    redemptions: paginated.redemptions,
    totalCount: paginated.totalCount,
    page: paginated.page,
    pageSize: paginated.pageSize,
    totalPages: paginated.totalPages,
    nameLookupFailures: paginated.nameLookupFailures,
    hasMore: paginated.hasMore,

    // Analytics
    analytics: analytics.data,

    // Filter options for dropdowns
    filterOptions: filterOptions.data,

    // Loading states
    isLoading: paginated.isLoading || analytics.isLoading,
    isLoadingRedemptions: paginated.isLoading,
    isLoadingAnalytics: analytics.isLoading,
    isLoadingFilterOptions: filterOptions.isLoading,
    isFetching: paginated.isFetching, // True when fetching but has cached data

    // Error states
    error: paginated.error || analytics.error,
    redemptionsError: paginated.error,
    analyticsError: analytics.error,

    // Refetch functions
    refetch: () => {
      paginated.refetch();
      analytics.refetch();
    },
    refetchRedemptions: paginated.refetch,
    refetchAnalytics: analytics.refetch,

    // Actions
    cancelVoucher: cancelVoucherMutation.mutateAsync,
    testVoucher: testVoucherMutation.mutateAsync,
    isCancelling: cancelVoucherMutation.isPending,
    isTesting: testVoucherMutation.isPending,

    // Utilities
    invalidate,
    user,

    // Export function - exports current page only
    // For full export, user should use dedicated export functionality
    exportCurrentPageToCSV: () => {
      if (paginated.redemptions.length > 0) {
        redemptionsService.exportRedemptionsToCSV(paginated.redemptions);
      }
    },
  };
}
